import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './components/UI/AntCustom/customGlobalStyles.scss';
import { store } from "./store/store";
import { Provider } from "react-redux";
import { createBrowserHistory } from 'history';
import { App } from "./App";
import { CustomRouter } from "./routes/CustomRouter";
import { LanguageWrapper } from "./components/highOrderComponents/LanguageWrapper/LanguageWrapper";
import { AirportApi } from "./API/ApiClasses/AirportApi";
import { AirportApiGetAirportFromAviapagesRequest } from "./proto/generated/api_entities_pb";
import { initAmplitude } from "./utils/analytics/amplitude";

export const browserHistory = createBrowserHistory();
const GA_MEASUREMENT_ID = import.meta.env.REACT_APP_GA_MEASUREMENT_ID;

initAmplitude();

const initGoogleAnalytics = () => {
  if (!GA_MEASUREMENT_ID) {
    return;
  }

  if (typeof window === "undefined") {
    return;
  }

  // Official Google Analytics 4 method
  // Exact copy of official snippet: https://developers.google.com/analytics/devguides/collection/ga4
  
  const w = window as any;
  
  // Initialize dataLayer
  w.dataLayer = w.dataLayer || [];
  
  // Create gtag function exactly as in official snippet
  // Use eval to create function declaration in global scope (as in HTML)
  if (!w.gtag) {
    // eslint-disable-next-line no-eval
    eval(`
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
    `);
  }
  
  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  
  // Call config immediately (official method) - commands will go to dataLayer
  w.gtag('js', new Date());
  w.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: true,
  });
  
  // Add script to head after config call
  document.head.appendChild(script);
};

initGoogleAnalytics();

// Initialize test functions for console
if (typeof window !== "undefined") {
  const w = window as any;
  
  /**
   * Test function to request airport information from Aviapages
   * Usage: window.aviapagesRequest('UAAA') or window.aviapagesRequest('EGLL')
   */
  w.aviapagesRequest = async (icaoCode: string) => {
    if (!icaoCode) {
      console.error('❌ ICAO airport code must be specified');
      console.log('Usage example: window.aviapagesRequest("UAAA")');
      return;
    }

    // Check for token
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      console.error('❌ Access token not found. Authorization required.');
      console.log('Please log in before using this function.');
      return;
    }

    console.log(`🛫 Requesting airport information from Aviapages: ${icaoCode}`);
    console.log(`🔑 Access token: ${accessToken.substring(0, 20)}...`);
    
    try {
      const request = new AirportApiGetAirportFromAviapagesRequest();
      request.setIcaocode(icaoCode.toUpperCase());
      
      const response = await AirportApi.getAirportFromAviapages(request);
      
      if (response.getResultresponse()?.getIssuccessful()) {
        const airportExtended = response.getAirportextended();
        console.log('✅ Successfully retrieved airport information:');
        console.log(airportExtended?.toObject());
        return airportExtended?.toObject();
      } else {
        const errorMessage = response.getResultresponse()?.getErrormessage() || 'Unknown error';
        console.error('❌ Error retrieving information:', errorMessage);
        console.error('Full response:', response.toObject());
        return null;
      }
    } catch (error: any) {
      console.error('❌ Exception during request:', error);
      console.error('Error details:', error.message || error);
      if (error.code === 16) { // UNAUTHENTICATED
        console.error('🔒 Authentication error. Please try logging in again.');
      }
      return null;
    }
  };
}

//
// if (process.env.REACT_APP_NODE_ENV === 'production') {
//   console.log = console.warn = console.error = () => {
//   };
// }

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <CustomRouter history={browserHistory}>
    <Provider store={store}>
      <LanguageWrapper>
        <App/>
      </LanguageWrapper>
    </Provider>
  </CustomRouter>
);

//TODO add service worker + unregister, see https://stackoverflow.com/questions/49604821/cache-busting-with-cra-react
